import type { ReactNode } from "react";
import Image from "next/image";
import { Button } from "@/components/ui/button";

export interface SpecSheetProps {
  title: string;
  note: string;
  sheetNumber: string;
  specs: readonly { label: string; value: string }[];
  chips: readonly string[];
  price: string;
  points: string;
  buyAction: { label: string; href: string };
  consultAction: ReactNode;
  legal: string;
}

export function SpecSheet({
  title,
  note,
  sheetNumber,
  specs,
  chips,
  price,
  points,
  buyAction,
  consultAction,
  legal,
}: SpecSheetProps) {
  return (
    <div className="overflow-hidden rounded-[18px] border border-hirudin/55 bg-card p-[3px] shadow-[0_18px_44px_rgba(85,19,28,0.12)]">
      <div className="overflow-hidden rounded-[14px] border border-hirudin/70 p-4">
        <div className="flex items-start justify-between gap-4">
          <h3 className="font-display text-[17px] leading-[1.5] font-semibold text-ink">
            {title}
            <span className="mt-0.5 block font-body text-xs leading-[18px] font-normal text-ink-3">
              {note}
            </span>
          </h3>
          <span className="max-w-[145px] text-right font-mono text-[10px] leading-4 tracking-[0.12em] text-hirudin">
            {sheetNumber}
          </span>
        </div>

        <div className="relative -mx-4 mt-4 h-52 overflow-hidden border-y border-hirudin/20 bg-hirudin-bg">
          <Image
            src="/images/catalog/hirudin/horizontal/slide-01.png"
            alt="水蛭素复合胶囊产品画面"
            fill
            sizes="(max-width: 480px) 100vw, 440px"
            loading="eager"
            className="object-cover object-center"
          />
          <span className="absolute inset-0 bg-linear-to-t from-hirudin-deep/55 via-transparent to-transparent" />
          <span className="absolute bottom-3 left-4 border border-paper-inverse/45 bg-hirudin-deep/65 px-2 py-1 font-mono text-[9px] tracking-[0.16em] text-paper-inverse backdrop-blur-sm">
            GICN · HIRUDIN ARCHIVE
          </span>
        </div>

        <dl className="mt-3 border-t border-line">
          {specs.map((spec) => (
            <div
              key={spec.label}
              className="flex border-b border-line py-2.5 text-[13px] leading-[1.6]"
            >
              <dt className="w-[76px] shrink-0 text-ink-3">{spec.label}</dt>
              <dd className="min-w-0 flex-1 text-ink">{spec.value}</dd>
            </div>
          ))}
        </dl>

        <ul className="mt-3 flex flex-wrap gap-2">
          {chips.map((chip) => (
            <li
              key={chip}
              className="bg-hirudin-bg px-2.5 py-1 text-[11px] leading-4 text-hirudin"
            >
              {chip}
            </li>
          ))}
        </ul>

        <div className="mt-4 flex items-center gap-3">
          <div className="shrink-0">
            <p className="font-body text-2xl leading-7 font-bold text-cinnabar tabular-nums">
              <span className="mr-px text-[0.65em]">¥</span>
              {price}
            </p>
            <p className="mt-0.5 text-[10px] leading-4 text-gilt">{points}</p>
          </div>
          <Button variant="buy" href={buyAction.href} className="min-w-0 flex-1">
            {buyAction.label}
          </Button>
        </div>

        <div className="mt-3 flex justify-center">{consultAction}</div>
        <p className="mt-3 text-[11px] leading-[1.6] text-ink-3">{legal}</p>
      </div>
    </div>
  );
}
